import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { QualificationEntity } from '../../../../entities/mdqx/doctor/qualification.entity';
import { GraphQlEffectService } from '../../../../services/graph-ql-effect.service';
import { HttpService } from '../../../../services/http.service';
import { BaseActionTypes } from '../../../actions/base.actions';
import { QualificationActions } from '../../../actions/mdqx/doctor/qualification.actions';
import { State } from '../../../index';
import { getToken } from '../../../selectors/base.selectors';
import { GetDoctorsRequest } from '../../../actions/mdqx/doctor/doctor.actions';

enum ErrorCodes {
    Load = 'Qualifikation Abrufen fehlgeschlagen',
    Modify = 'Qualifikation Bearbeiten fehlgeschlagen',
    Remove = 'Qualifikation Löschen fehlgeschlagen',
    Add = 'Qualifikation Hinzufügen fehlgeschlagen',
}

@Injectable()
export class QualificationEffects {
    errors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QualificationActions.loadFailure, QualificationActions.putFailure, QualificationActions.changeFailure, QualificationActions.deleteFailure),
            map((action) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: action.ToasterMessage,
                        Err: action.Errors,
                    },
                }),
            ),
        ),
    );

    Get$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(QualificationActions.loadRequest),
                withLatestFrom(this.store$.select(getToken)),
                switchMap(([{ Payload }, token]) => {
                    const queryString = `query {
                    qualification{
                        ${QualificationEntity.GqlFields.join(',')}
                    }
                }`;
                    return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }, '', environment.mdqxGatewayApiUrl).pipe(
                        tap((res) => {
                            this.store$.dispatch(
                                res && res.qualification
                                    ? QualificationActions.loadSuccess({
                                          Payload: res.qualification.map((val) => QualificationEntity.fromBackend(val)),
                                      })
                                    : QualificationActions.loadFailure({ ToasterMessage: ErrorCodes.Load, Errors: 'wrong response qualification' }),
                            );
                        }),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                QualificationActions.loadFailure({
                                    ToasterMessage: ErrorCodes.Load,
                                    Errors: err,
                                }),
                            );
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );
    Put$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QualificationActions.changeRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  changeQualification(id: ${Payload.id} name: "${Payload.name}"){
                  ${QualificationEntity.GqlFields.join(',')}
                  }
                }
                `;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (res?.changeQualification) {
                            return QualificationActions.changeSuccess({ Payload: QualificationEntity.fromBackend(res.changeQualification) });
                        } else {
                            return QualificationActions.changeFailure({
                                ToasterMessage: ErrorCodes.Modify,
                                Errors: 'wrong response changeQualification',
                            });
                        }
                    }),
                    catchError((err, caught) => {
                        return of(
                            QualificationActions.changeFailure({
                                ToasterMessage: ErrorCodes.Modify,
                                Errors: err,
                            }),
                        );
                    }),
                );
            }),
        ),
    );
    Delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QualificationActions.deleteRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  deleteQualification(id: ${Payload.id})
                }
                `;
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    concatMap((res) => {
                        if (res?.deleteQualification) {
                            return [QualificationActions.deleteSuccess({ Payload: Payload.id }), new GetDoctorsRequest()];
                        } else {
                            return [
                                QualificationActions.deleteFailure({
                                    ToasterMessage: ErrorCodes.Remove,
                                    Errors: 'wrong response deleteQualification',
                                }),
                            ];
                        }
                    }),
                    catchError((err, caught) => {
                        return of(
                            QualificationActions.deleteFailure({
                                ToasterMessage: ErrorCodes.Remove,
                                Errors: err,
                            }),
                        );
                    }),
                );
            }),
        ),
    );
    constructor(private actions$: Actions, private store$: Store<State>, private gatewayHttpService: HttpService, private graphQlEffectService: GraphQlEffectService) {}
}
